import { c as _c } from "react/compiler-runtime";
import useTranslation from '@/hooks/useTranslation';
import classNames from '@/utils/classNames';
import ChevronLeftIcon from '@bloomberg/web-icons-react/ChevronLeft';
import ChevronRightIcon from '@bloomberg/web-icons-react/ChevronRight';
import FirstPageIcon from '@bloomberg/web-icons-react/FirstPage';
import LastPageIcon from '@bloomberg/web-icons-react/LastPage';
import type { AnchorHTMLAttributes, FC, MouseEvent, MouseEventHandler } from 'react';
import type { FCh } from 'react-fc';
import styles from './Pagination.module.css';
export const PAGE_PLACEHOLDER = '{page}';

/**
 * A bar to navigate paginated information.
 */
export const Pagination: FC<IPaginationProps> = props => {
  const $ = _c(16);
  const {
    t
  } = useTranslation("common");
  if (props.total === 0) {
    return null;
  }
  let t0;
  let t1;
  let t2;
  let t3;
  if ($[0] !== props || $[1] !== t) {
    const lastPage = props.total ? getMaximumPage(props.total, props.pageSize) : undefined;
    t3 = styles.wrap;
    if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
      t0 = classNames("cds-t-body", styles.pagination);
      $[6] = t0;
    } else {
      t0 = $[6];
    }
    if ($[7] !== props) {
      t1 = !props.hiddenIntro && props.total !== 40000 && <PaginationIntro {...props} />;
      $[7] = props;
      $[8] = t1;
    } else {
      t1 = $[8];
    }
    t2 = (!props.total || props.total > props.pageSize) && <ol className={styles.buttons}><Item key="first" disabled={props.currentPage <= 1} url={getUrl(props.urlTemplate, 1)} aria-label="1" onClick={e => props.onChange && props.onChange(e, 1)}><FirstPageIcon /></Item><Item key="prev" disabled={props.currentPage <= 1} url={getUrl(props.urlTemplate, props.currentPage - 1)} rel="prev" aria-label={t("Pagination.previous_page_link")} onClick={e_0 => props.onChange && props.onChange(e_0, props.currentPage - 1)}><ChevronLeftIcon /></Item>{!props.hiddenPageSelector && <Pages {...props} />}<Item key="next" disabled={props.total !== undefined && props.currentPage >= getMaximumPage(props.total, props.pageSize)} url={getUrl(props.urlTemplate, props.currentPage + 1)} rel="next" aria-label={t("Pagination.next_page_link")} onClick={e_1 => props.onChange && props.onChange(e_1, props.currentPage + 1)}><ChevronRightIcon /></Item>{lastPage && !props.hiddenLastPage && <Item key="last" disabled={props.currentPage >= lastPage} url={getUrl(props.urlTemplate, lastPage)} aria-label={lastPage.toString()} onClick={e_2 => props.onChange && props.onChange(e_2, lastPage)}><LastPageIcon /></Item>}</ol>;
    $[0] = props;
    $[1] = t;
    $[2] = t0;
    $[3] = t1;
    $[4] = t2;
    $[5] = t3;
  } else {
    t0 = $[2];
    t1 = $[3];
    t2 = $[4];
    t3 = $[5];
  }
  let t4;
  if ($[9] !== t0 || $[10] !== t1 || $[11] !== t2) {
    t4 = <nav className={t0}>{t1}{t2}</nav>;
    $[9] = t0;
    $[10] = t1;
    $[11] = t2;
    $[12] = t4;
  } else {
    t4 = $[12];
  }
  let t5;
  if ($[13] !== t3 || $[14] !== t4) {
    t5 = <div className={t3}>{t4}</div>;
    $[13] = t3;
    $[14] = t4;
    $[15] = t5;
  } else {
    t5 = $[15];
  }
  return t5;
};
export const PaginationIntro: FC<Pick<IPaginationProps, 'pageSize' | 'currentPage' | 'total'>> = t0 => {
  const $ = _c(5);
  const {
    pageSize,
    currentPage,
    total
  } = t0;
  const formatter = new Intl.NumberFormat();
  const {
    t
  } = useTranslation("common");
  if (total) {
    const t1 = formatter.format(getFrom(pageSize, currentPage));
    let t2;
    if ($[0] !== currentPage || $[1] !== pageSize) {
      t2 = getTo(pageSize, currentPage);
      $[0] = currentPage;
      $[1] = pageSize;
      $[2] = t2;
    } else {
      t2 = $[2];
    }
    return <p className="cds-t-body">{t("Pagination.intro_with_known_total", {
        from: t1,
        to: formatter.format(Math.min(total, t2)),
        total: formatter.format(total)
      })}</p>;
  }
  const t1 = "cds-t-body";
  const t2 = t("Pagination.intro_without_known_total", {
    from: formatter.format(getFrom(pageSize, currentPage)),
    to: formatter.format(getTo(pageSize, currentPage))
  });
  let t3;
  if ($[3] !== t2) {
    t3 = <p className={t1}>{t2}</p>;
    $[3] = t2;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  return t3;
};
const Pages = t0 => {
  const $ = _c(45);
  const {
    currentPage,
    pageSize,
    urlTemplate,
    total,
    onChange
  } = t0;
  const {
    t
  } = useTranslation("common");
  let t1;
  let t10;
  let t2;
  let t3;
  let t4;
  let t5;
  let t6;
  let t7;
  let t8;
  let t9;
  if ($[0] !== currentPage || $[1] !== onChange || $[2] !== pageSize || $[3] !== t || $[4] !== total || $[5] !== urlTemplate) {
    t10 = Symbol.for("react.early_return_sentinel");
    bb0: {
      const formatter = new Intl.NumberFormat();
      if (total === undefined) {
        t10 = null;
        break bb0;
      }
      const maxPage = getMaximumPage(total, pageSize);
      t3 = styles.selector;
      t4 = "get";
      let t11;
      if ($[16] !== currentPage || $[17] !== urlTemplate) {
        t11 = getUrl(urlTemplate, currentPage).split("?");
        $[16] = currentPage;
        $[17] = urlTemplate;
        $[18] = t11;
      } else {
        t11 = $[18];
      }
      t5 = t11[0];
      if ($[19] !== maxPage || $[20] !== onChange) {
        t6 = e => {
          if (onChange) {
            e.preventDefault();
            const formData = new FormData(e.currentTarget as HTMLFormElement);
            const pageNum = parseInt(formData.get("page") as string);
            if (!isNaN(pageNum) && pageNum >= 1 && pageNum <= maxPage) {
              onChange(e as unknown as MouseEvent<HTMLAnchorElement>, pageNum);
            }
          }
        };
        $[19] = maxPage;
        $[20] = onChange;
        $[21] = t6;
      } else {
        t6 = $[21];
      }
      if ($[22] !== currentPage || $[23] !== urlTemplate) {
        bb1: {
          const url = getUrl(urlTemplate, currentPage);
          const queryStringIdx = url.indexOf("?");
          if (queryStringIdx > -1) {
            const params = new URLSearchParams(url.substring(queryStringIdx + 1));
            const t12 = Array.from(params.entries()).filter(_temp);
            let t13;
            if ($[25] !== t12) {
              t13 = t12.map(_temp2);
              $[25] = t12;
              $[26] = t13;
            } else {
              t13 = $[26];
            }
            t7 = t13;
            break bb1;
          }
          t7 = null;
        }
        $[22] = currentPage;
        $[23] = urlTemplate;
        $[24] = t7;
      } else {
        t7 = $[24];
      }
      let t12;
      if ($[27] !== t) {
        t12 = t("Pagination.current_page");
        $[27] = t;
        $[28] = t12;
      } else {
        t12 = $[28];
      }
      if ($[29] !== currentPage || $[30] !== maxPage || $[31] !== t12) {
        t8 = <input aria-label={t12} type="number" max={maxPage} min={1} name="page" required={true} defaultValue={currentPage} key={currentPage} />;
        $[29] = currentPage;
        $[30] = maxPage;
        $[31] = t12;
        $[32] = t8;
      } else {
        t8 = $[32];
      }
      t9 = " ";
      t1 = "/ ";
      t2 = formatter.format(maxPage);
    }
    $[0] = currentPage;
    $[1] = onChange;
    $[2] = pageSize;
    $[3] = t;
    $[4] = total;
    $[5] = urlTemplate;
    $[6] = t1;
    $[7] = t10;
    $[8] = t2;
    $[9] = t3;
    $[10] = t4;
    $[11] = t5;
    $[12] = t6;
    $[13] = t7;
    $[14] = t8;
    $[15] = t9;
  } else {
    t1 = $[6];
    t10 = $[7];
    t2 = $[8];
    t3 = $[9];
    t4 = $[10];
    t5 = $[11];
    t6 = $[12];
    t7 = $[13];
    t8 = $[14];
    t9 = $[15];
  }
  if (t10 !== Symbol.for("react.early_return_sentinel")) {
    return t10;
  }
  let t11;
  if ($[33] !== t1 || $[34] !== t2) {
    t11 = <span>{t1}{t2}</span>;
    $[33] = t1;
    $[34] = t2;
    $[35] = t11;
  } else {
    t11 = $[35];
  }
  let t12;
  if ($[36] !== t11 || $[37] !== t3 || $[38] !== t4 || $[39] !== t5 || $[40] !== t6 || $[41] !== t7 || $[42] !== t8 || $[43] !== t9) {
    t12 = <form className={t3} method={t4} action={t5} onSubmit={t6}>{t7}{t8}{t9}{t11}</form>;
    $[36] = t11;
    $[37] = t3;
    $[38] = t4;
    $[39] = t5;
    $[40] = t6;
    $[41] = t7;
    $[42] = t8;
    $[43] = t9;
    $[44] = t12;
  } else {
    t12 = $[44];
  }
  return t12;
};
export interface IPaginationProps {
  /** URL with template "{page}" which will be replaced as required */
  urlTemplate: string;
  /** The current page being viewed */
  currentPage: number;
  /** How many items are being shown per page? */
  pageSize: number;
  /** Total number of results (if known) */
  total?: number;
  /** Optional callback for when the page changes. Use preventDefault to stop navigation */
  onChange?: (event: MouseEvent<HTMLAnchorElement>, newPageNumber: number) => void;
  /** Show/hide intro text */
  hiddenIntro?: boolean;
  /** Until we have removed Sphinx, we don't show the total number of results unless there's an active Filter*/
  activeFilters?: boolean;
  /** Show/hide the last page button */
  hiddenLastPage?: boolean;
  /** Show/hide the page selector */
  hiddenPageSelector?: boolean;
}
const Item: FCh<IItemProps> = t0 => {
  const $ = _c(16);
  let children;
  let onClick;
  let rel;
  let rest;
  let t1;
  let url;
  if ($[0] !== t0) {
    ({
      disabled: t1,
      onClick,
      url,
      rel,
      children,
      ...rest
    } = t0);
    $[0] = t0;
    $[1] = children;
    $[2] = onClick;
    $[3] = rel;
    $[4] = rest;
    $[5] = t1;
    $[6] = url;
  } else {
    children = $[1];
    onClick = $[2];
    rel = $[3];
    rest = $[4];
    t1 = $[5];
    url = $[6];
  }
  const disabled = t1 === undefined ? false : t1;
  if (disabled) {
    let t2;
    if ($[7] !== children || $[8] !== rest) {
      t2 = <li><span className="cds-button cds-button--tertiary cds-button--icon cds-disabled" {...rest}>{children}</span></li>;
      $[7] = children;
      $[8] = rest;
      $[9] = t2;
    } else {
      t2 = $[9];
    }
    return t2;
  }
  let t2;
  if ($[10] !== children || $[11] !== onClick || $[12] !== rel || $[13] !== rest || $[14] !== url) {
    t2 = <li><a className="cds-button cds-button--tertiary cds-button--icon" href={url} rel={rel} onClick={onClick} {...rest}>{children}</a></li>;
    $[10] = children;
    $[11] = onClick;
    $[12] = rel;
    $[13] = rest;
    $[14] = url;
    $[15] = t2;
  } else {
    t2 = $[15];
  }
  return t2;
};
export const getUrl = (template: string, page: number) => template.replace(PAGE_PLACEHOLDER, page.toString());
const getMaximumPage = (total: number, pageSize: number) => Math.ceil(total / pageSize);
const getFrom = (pageSize: number, currentPage: number) => pageSize * (currentPage - 1) + 1;
const getTo = (pageSize: number, currentPage: number) => pageSize * currentPage;
interface IItemProps extends AnchorHTMLAttributes<HTMLAnchorElement> {
  url: string;
  disabled?: boolean;
  onClick?: MouseEventHandler<HTMLAnchorElement>;
  rel?: AnchorHTMLAttributes<HTMLAnchorElement>['rel'];
}
function _temp(t0) {
  const [key] = t0;
  return key !== "page";
}
function _temp2(t0) {
  const [key_0, value] = t0;
  return <input key={key_0} type="hidden" name={key_0} value={value} />;
}